/******************************************************************************\
 * :$
 *
 * Copyright(c) 2022 SAS Institute Inc., Cary, NC, USA. All Rights Reserved.
 *
 * Name: IApplication
 *
 * Purpose: model data for submission
 *
 * Author: craig (Craig.Simpson@sas.com)
 *
 * Support: SAS(r) Solutions OnDemand
 *
 * Input:
 *
 * Output:
 *
 * Parameters: (if applicable)
 *
 * Dependencies/Assumptions:
 *
 * Usage:
 *
 * History:
 * ddmmmyyyy userid description (Change Code)
 * 09Feb2022 craig File created.
 \******************************************************************************/
import {IUser} from "../user/IUser";


export interface IOrgDataResponse {
    SubmissionID: number,
    QuestionID: string,
    ResponseTitle: string,
    Response: string
}

export interface IOrgData {
    SubmissionID: number,
    QuestionID: string,
    QuestionTitle: string,
    ResponseList: IOrgDataResponse[]
}

export interface ISubmissionList {
    SubmissionID: number,
    Organization: string,
}

export interface IReviewerList {
    UserID: number,
    FirstName?: string,
    LastName?: string,
    EmailAddress?: string
}

export interface ISubmission {
    SubmissionID: number,
    SubmissionTitle: string,
    ApplicationID: number,
    OrganizationID: number,
    Organization: string,
    CreationDate: Date,
    SubmissionDate: Date,
    OrgDataMap: Map<string, IOrgData>
}

export interface ITeamSubmissionLinkage {
    TeamID: number,
    SubmissionID: number,
    Submission: ISubmission
}

export interface ITeam {
    TeamID: number,
    TeamName: string,
    StartDate: Date,
    EndDate: Date,
    GroupDate: Date,
    JumpStart: string,
    TeamSubmissionLinkage?: ITeamSubmissionLinkage[],
    TeamUserLinkage?: ITeamUserLinkage[]
}

export interface ITeamUserLinkage {
    TeamID: number,
    UserID: number,
    Submitted: boolean,
    SubmittedAt: Date,
    ReviewStatus: Map<number, number>,
    GSUser?: IUser,
    Team?: ITeam,
    TeamUserSubmissionLinkage?: ITeamUserSubmissionLinkage[]
}

export interface ISubmissionUserLinkage {
    SubmissionID: number,
    UserID: number,
    CanEdit: boolean,
    Submission: ISubmission
}

export interface IApplicationData {
    QuestionKey: string,
    ApplicationID: number,
    Response?: string
}

export interface IGoldStandardQuestion {
    QuestionID: string,
    QuestionKey: string,
    QuestionOrder: number,
    QuestionType: string,
    ShowInAdmin: string,
    Reporting: string,
    QuestionText: string,
    NumQuestions: number,
    NumAnswered: number,
    Question: IQuestion
}

export interface IReviewerComment {
    SubmissionID: number,
    UserID: number,
    Pillar: string,
    TeamID: number,
    Concerns: string,
    Accolades: string,
    CreatedAt: Date,
    UpdatedAt: Date,
    GSUser?: IUser
}

export enum Pillar {
    P1 = "P1",
    P2 = "P2",
    P3 = "P3",
    P5 = "P5",
    ESSAY = "ESSAY",
    GeneralComments = "COMMENTS"
}

export interface IPillarData {
    Pillar: Pillar,
    Questions: IGoldStandardQuestion[],
    Comments?: IReviewerComment[]
}

export interface IGoldStandardPillar {
    Submission: ISubmission,
    Team: ITeam,
    Pillar: IPillarData,
    TeamSubmissionUserLinkage: ITeamUserSubmissionLinkage
}

export interface ITeamUserSubmissionLinkage {
    TeamID: number,
    SubmissionID: number,
    UserID: number,
    Submitted: boolean,
    CreatedAt: Date,
    UpdatedAt: Date,
    ReviewerComment: IReviewerComment[]
}

export interface IGoldStandardPillars {
    Submission: ISubmission,
    Team: ITeam,
    Pillar1: IPillarData,
    Pillar2: IPillarData,
    Pillar3: IPillarData,
    Pillar4: IPillarData,
    Pillar5: IPillarData,
    Essay: IPillarData,
    Comments: IPillarData
}

export interface IGoldStandardPillar {
    Submission: ISubmission,
    Team: ITeam,
    Pillar: IPillarData,
}

export interface IBaseQuestion {
    SubmissionID: number,
    QuestionID: string,
    QuestionTitle: string
}

export interface IOrgData {
    BaseQuestion: IBaseQuestion,
    ResponseList: IOrgDataResponse[]
}

export interface IResponse {
    SubmissionID: number,
    QuestionID: string,
    ResponseTitle: string,
    Response: string
}

export interface IQuestion {
    SubmissionID: number,
    QuestionID: string,
    QuestionTitle: string
    Responses: IResponse[]
}




